"use client"

import React, {useEffect, useState} from "react";
import {BASE} from "@/services/Api";
import {AxiosError} from "axios";
import ErrorHandler from "@/components/utils/ErrorHandler";
import CloseButton from "@/components/tailwindComponents/button/CloseButton";
import {useSearchParams, useRouter} from 'next/navigation';
import FiltersWrapper from "@/components/filters/Filters";
import InfiniteScroll from "react-infinite-scroll-component";
import Spinner from "@/components/tailwindComponents/Spinner";
import {PackageType} from "@/types/Package";
import {DEFAULT_PAGE_SIZE} from "@/constants";
import ProductList from "@/components/product/ProductList";
import LPC from "@/components/landing/LPCCARD";
import SubmitButton from "@/components/tailwindComponents/button/SubmitButton";
import LoadingDots from "@/components/utils/LoadingDots";
import {toPersianDigits} from "@/components/utils/PersianNumber";
import InputSelect from "@/components/input-fields/InputSelect";

interface FiltersType {
    [key: string]: string | number | boolean | undefined;
}

// مدت انتظار بعد از آخرین کاراکتر تایپ‌شده تا ارسال ریکوئست جستجو
const SEARCH_DEBOUNCE_MS = 500;
// حداقل تعداد کاراکتر برای جستجو؛ کمتر از این یعنی بدون فیلتر جستجو
const SEARCH_MIN_LENGTH = 3;

const orderOptions = [
    {key: "-id", name: "جدیدترین"},
    {key: "id", name: "قدیمی‌ترین"},
    {key: "price", name: "ارزان‌ترین"},
    {key: "-price", name: "گران‌ترین"},
];

const Package = () => {
    const [data, setData] = useState<PackageType[]>([])
    const [page, setPage] = useState(1);
    const pageSize = DEFAULT_PAGE_SIZE;
    const [loading, setLoading] = useState(true)
    const [order, setOrder] = useState("")
    const [filters, setFilters] = useState<FiltersType>({})
    const [error, setError] = useState<AxiosError | null>(null);
    const [count, setCount] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<number | string>("");
    // متن تایپ‌شده در input جستجو (فوری) — فیلتر name با تاخیر از روی این مقدار ست می‌شود
    const [searchText, setSearchText] = useState("");

    const searchParams = useSearchParams();
    const router = useRouter();

    useEffect(() => {
        const hasSearch = searchParams.has('search');
        if (hasSearch) {
            const paramValue: string = searchParams.get('search') ?? "";
            setFilters({name: paramValue});
            setSearchText(paramValue);
            const newParams = new URLSearchParams(searchParams.toString());
            newParams.delete('search');
            const newUrl = window.location.pathname + (newParams.toString() ? `?${newParams}` : '');
            router.replace(newUrl);
        }
    }, [searchParams]);

    const convertFiltersToString = (filterObject: FiltersType): string => {
        const output: string[] = [];
        Object.entries(filterObject).forEach((filter) => {
            if (filter[1] || filter[1] === 0) {
                switch (filter[0]) {
                    case("name"):
                        output.push(`fulltext=${filter[1]}`)
                        break;
                    case("status"):
                        output.push(`filter_status=${filter[1]}`)
                        break;
                    default:
                        break;
                }
            }
        })
        return output.join('&');
    }


    const getPackages = async () => {
        setLoading(true)
        const filterQuery = convertFiltersToString(filters)
        try {
            const response = await BASE.PACKAGE.list(page, pageSize, filterQuery, order)
            const json = await response.data;
            if (json) {
                if (page === 1) {
                    setData(json.results);
                } else {
                    setData(prev => [...prev, ...json.results]);
                }

                setCount(json.count);
                setHasMore(json.count > page * pageSize);
            }
        } catch (e) {
            if (e instanceof AxiosError) {
                setError(e);
            } else {
                setError(null);
            }
            console.error("Unexpected error:", e);
        } finally {
            setLoading(false)
        }
    }

    // هر بار page تغییر کنه، دیتا گرفته بشه
    useEffect(() => {
        getPackages();
    }, [page, order, filters]);

    // اگر order یا filter تغییر کنن، ریست کن
    useEffect(() => {
        setPage(1);
        setData([]);
        setHasMore(true);
    }, [filters, order]);

    // debounce جستجو: فقط وقتی کاربر SEARCH_DEBOUNCE_MS تایپ نکرد، فیلتر name آپدیت و ریکوئست زده می‌شود
    useEffect(() => {
        const timer = setTimeout(() => {
            const trimmed = searchText.trim();
            const value = trimmed.length >= SEARCH_MIN_LENGTH ? trimmed : "";

            setFilters((prevFilters) => {
                // اگر مقدار عوض نشده، state را تغییر نده تا ریکوئست اضافه زده نشود
                if (String(prevFilters.name ?? "") === value) return prevFilters;

                const updatedFilters = {...prevFilters};
                if (value) {
                    updatedFilters.name = value;
                } else {
                    delete updatedFilters.name;
                }
                return updatedFilters;
            });
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [searchText]);

    const loadMore = () => {
        if (!loading && hasMore) {
            setPage(prev => prev + 1);
        }
    }

    const statusOptions = [
        {label: "همه محصولات", value: ""},
        {label: "محصولات فعال", value: 1},
    ]

    const handleStatusChange = (value: number) => {
        setSelectedStatus(value);

        if (!value) {
            setFilters(prev => {
                const updated = {...prev};
                delete updated.status;
                return updated;
            });
        } else {
            setFilters(prev => ({
                ...prev,
                status: value
            }));
        }
    };

    const resetFilters = () => {
        setFilters({})
        setSearchText("")
        setSelectedStatus("")
    }

    return (
        <div className="container mx-auto py-12">
            <ErrorHandler error={error}/>
            <div className="flex w-full space-x-0 lg:space-x-4">
                {/* فیلترها */}
                <FiltersWrapper isOpen={isOpen} onClose={() => setIsOpen(false)}>
                    <div>
                        <input
                            type="text"
                            className="p-2 border rounded-md w-full text-sm"
                            placeholder="جستجو در پکیج‌ها..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                        {searchText.trim().length > 0 && searchText.trim().length < SEARCH_MIN_LENGTH && (
                            <p className="text-xs text-gray-500 mt-1">
                                برای جستجو حداقل {toPersianDigits(SEARCH_MIN_LENGTH)} کاراکتر وارد کنید
                            </p>
                        )}
                    </div>

                    <InputSelect
                        name="status"
                        label="وضعیت"
                        options={statusOptions}
                        value={selectedStatus}
                        onChange={handleStatusChange}
                        error=""
                        firstOption=""
                    />

                    <div className="flex-grow"></div>
                    <div className="flex justify-center w-full pt-12">
                        <div className="absolute bottom-1">
                            <div className="inline-flex pb-2 space-x-2">
                                <div className="block lg:hidden">
                                    <SubmitButton
                                        text={loading ?
                                            <div className="py-1"><LoadingDots/></div> :
                                            (count > 0 ? `مشاهده ${toPersianDigits(count)} محصول` : "محصول یافت نشد")
                                        }
                                        onClick={() => setIsOpen(false)}
                                        disabled={loading || count === 0}
                                        className={count === 0 ? "text-gray-500 border-gray-400" : ""}
                                    />
                                </div>
                                <CloseButton
                                    text="ریست"
                                    onClick={resetFilters}
                                />
                            </div>
                        </div>
                    </div>
                </FiltersWrapper>

                {/* لیست پکیج‌ها */}
                <div className="w-full lg:w-3/4">
                    <InfiniteScroll
                        dataLength={data.length}
                        next={loadMore}
                        hasMore={hasMore}
                        loader={<div className="flex w-full justify-center py-12"><Spinner className="size-12"/></div>}
                        className="custom-scroll-wrapper"
                    >
                        <ProductList<PackageType>
                            data={data}
                            count={count}
                            orderOptions={orderOptions}
                            setOrder={setOrder}
                            loading={loading}
                            setIsOpenFilters={prop => setIsOpen(prop)}
                            initialSelected=""
                            initialSelectedName=""
                            renderItem={(item) => (
                                <LPC
                                    item={item}
                                    link="/package/"
                                    keyId={item.id}
                                    isPackage
                                />
                            )}
                        />
                    </InfiniteScroll>
                </div>
            </div>
        </div>
    )
}
export default Package;
